import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Globe, Layout, FileText, CheckCircle2, Plus, ExternalLink,
  Eye, MousePointerClick, TrendingUp, Sparkles, Copy, MessageSquare, QrCode,
  Download, Check, Trash2, Code, ShieldCheck, Folder, Clock, List,
  LayoutGrid, Search, MoreVertical, Home, ChevronDown, ShoppingBag,
  Video, BarChart3, BookOpen, Layers, Edit3, Settings, Bot, RefreshCw
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Master Admin default websites
const ADMIN_SHOWCASE_WEBSITES = [
  {
    id: "w1",
    name: "Colibri Commercial Media Hub",
    domain: "media.weddingsthyzfoto.com",
    pages: 5,
    visitors: 3420,
    status: "Published",
    lastUpdated: "Today 10:14 AM",
  },
  {
    id: "w2",
    name: "Luxury Weddings & Events Studio",
    domain: "events.weddingsthyzfoto.com",
    pages: 3,
    visitors: 1890,
    status: "Published",
    lastUpdated: "Yesterday",
  },
];

const ADMIN_SHOWCASE_STORES = [
  {
    id: "s1",
    name: "Colibri Creative Digital Assets",
    currency: "USD",
    products: 4,
    revenue: "$3,450.00",
    orders: 34,
    status: "Active",
    lastUpdated: "Today",
  },
];

const ADMIN_SHOWCASE_WEBINARS = [
  {
    id: "wb1",
    name: "Scale Your Studio to $20k/mo with AI Automations",
    host: "Master Admin & Marcus Vance",
    time: "Tomorrow at 2:00 PM EST",
    registrants: 184,
    status: "Live Scheduled",
  },
];

const ADMIN_SHOWCASE_BLOGS = [
  {
    id: "b1",
    title: "10 Ways AI Automations Save Creative Studios 20+ Hours Weekly",
    author: "Master Admin",
    category: "AI & Growth",
    views: 1420,
    date: "Oct 28, 2026",
    status: "Published",
  },
  {
    id: "b2",
    title: "The High-Converting Funnel Formula for Photography & Media",
    author: "Marcus Vance",
    category: "Marketing",
    views: 980,
    date: "Oct 24, 2026",
    status: "Published",
  },
];

// Master Admin reference funnels matching user reference screenshot exactly
const ADMIN_SHOWCASE_FUNNELS = [
  {
    id: "f1",
    name: "New Landing (RP)",
    lastUpdated: "May 30, 2026 02:52 AM",
    stepsCount: "1 Step",
    visitors: 1420,
    optins: 384,
    status: "Published",
    url: "https://deck.weddingsthyzfoto.com/funnel/new-landing-rp",
  },
  {
    id: "f2",
    name: "RealHUB Menu",
    lastUpdated: "Apr 25, 2026 01:30 AM",
    stepsCount: "1 Step",
    visitors: 890,
    optins: 215,
    status: "Published",
    url: "https://deck.weddingsthyzfoto.com/funnel/realhub-menu",
  },
];

export default function Sites() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") || "funnels";

  const isMasterOrAdmin =
    user?.role === "master" ||
    user?.role === "admin" ||
    user?.email === "colibrisocialclub@gmail.com" ||
    user?.email === "thyzfoto@gmail.com" ||
    user?.email === "contact@amanbiswalthakur.in";

  // Account isolated funnels - Master Admin sees reference items, new accounts start clean & empty
  const [funnels, setFunnels] = useState(() => {
    if (!user) return isMasterOrAdmin ? ADMIN_SHOWCASE_FUNNELS : [];
    const saved = localStorage.getItem(`launchdeck_funnels_${user.id}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {}
    }
    return isMasterOrAdmin ? ADMIN_SHOWCASE_FUNNELS : [];
  });

  useEffect(() => {
    if (user?.id) {
      localStorage.setItem(`launchdeck_funnels_${user.id}`, JSON.stringify(funnels));
    }
  }, [funnels, user]);

  const [websites, setWebsites] = useState(() => isMasterOrAdmin ? ADMIN_SHOWCASE_WEBSITES : []);
  const [stores, setStores] = useState(() => isMasterOrAdmin ? ADMIN_SHOWCASE_STORES : []);
  const [webinars, setWebinars] = useState(() => isMasterOrAdmin ? ADMIN_SHOWCASE_WEBINARS : []);
  const [blogs, setBlogs] = useState(() => isMasterOrAdmin ? ADMIN_SHOWCASE_BLOGS : []);

  // Visual Builder Modal state
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderSite, setBuilderSite] = useState(null);
  const [newBlogOpen, setNewBlogOpen] = useState(false);
  const [blogForm, setBlogForm] = useState({ title: "", category: "Growth", author: "Master Admin", excerpt: "" });

  const handleCreateBlog = (e) => {
    e.preventDefault();
    if (!blogForm.title) { toast.error("Please enter blog title"); return; }
    const newB = {
      id: `b-${Date.now()}`,
      title: blogForm.title,
      category: blogForm.category,
      author: blogForm.author,
      views: 0,
      date: "Just now",
      status: "Published"
    };
    setBlogs([newB, ...blogs]);
    setNewBlogOpen(false);
    setBlogForm({ title: "", category: "Growth", author: "Master Admin", excerpt: "" });
    toast.success(`Blog post "${newB.title}" published!`);
  };
  const [searchFunnel, setSearchFunnel] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Modals state
  const [newFunnelOpen, setNewFunnelOpen] = useState(false);
  const [funnelForm, setFunnelForm] = useState({ name: "", path: "" });
  const [editingFunnel, setEditingFunnel] = useState(null);
  const [aiFunnelOpen, setAiFunnelOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);

  // Additional sites modals
  const [newWebsiteOpen, setNewWebsiteOpen] = useState(false);
  const [websiteForm, setWebsiteForm] = useState({ name: "", domain: "studio.colibri.com", pages: 4 });
  const [newStoreOpen, setNewStoreOpen] = useState(false);
  const [storeForm, setStoreForm] = useState({ name: "", currency: "USD", productCount: 3 });
  const [newWebinarOpen, setNewWebinarOpen] = useState(false);
  const [webinarForm, setWebinarForm] = useState({ name: "", host: "Colibri Master", time: "Tomorrow at 2:00 PM" });

  const handleCreateWebsite = (e) => {
    e.preventDefault();
    if (!websiteForm.name) { toast.error("Please enter website name"); return; }
    const newW = {
      id: `w-${Date.now()}`,
      name: websiteForm.name,
      domain: websiteForm.domain,
      pages: Number(websiteForm.pages) || 1,
      visitors: 0,
      status: "Published",
      lastUpdated: "Just now",
    };
    setWebsites([newW, ...websites]);
    setNewWebsiteOpen(false);
    setWebsiteForm({ name: "", domain: "studio.colibri.com", pages: 4 });
    toast.success(`Website "${newW.name}" created and published!`);
  };

  const handleCreateStore = (e) => {
    e.preventDefault();
    if (!storeForm.name) { toast.error("Please enter store name"); return; }
    const newS = {
      id: `s-${Date.now()}`,
      name: storeForm.name,
      currency: storeForm.currency,
      products: Number(storeForm.productCount) || 1,
      status: "Active",
      lastUpdated: "Just now",
    };
    setStores([newS, ...stores]);
    setNewStoreOpen(false);
    setStoreForm({ name: "", currency: "USD", productCount: 3 });
    toast.success(`Store "${newS.name}" initialized with Stripe checkout!`);
  };

  const handleCreateWebinar = (e) => {
    e.preventDefault();
    if (!webinarForm.name) { toast.error("Please enter webinar title"); return; }
    const newWb = {
      id: `wb-${Date.now()}`,
      name: webinarForm.name,
      host: webinarForm.host,
      time: webinarForm.time,
      registrants: 0,
      status: "Scheduled",
    };
    setWebinars([newWb, ...webinars]);
    setNewWebinarOpen(false);
    setWebinarForm({ name: "", host: "Colibri Master", time: "Tomorrow at 2:00 PM" });
    toast.success(`Webinar "${newWb.name}" scheduled!`);
  };

  // Chat widget settings
  const [copiedCode, setCopiedCode] = useState(false);
  const chatEmbedScript = `<script src="https://deck.weddingsthyzfoto.com/widget/chat.js" data-site-id="wh-78923" async></script>`;

  const handleCreateFunnel = (e) => {
    e.preventDefault();
    if (!funnelForm.name) {
      toast.error("Please enter a funnel name");
      return;
    }
    const newF = {
      id: "f-" + Date.now(),
      name: funnelForm.name,
      lastUpdated: "Just now",
      stepsCount: "1 Step",
      visitors: 0,
      optins: 0,
      status: "Draft",
      url: `https://deck.weddingsthyzfoto.com/funnel/${funnelForm.path || funnelForm.name.toLowerCase().replace(/\s+/g, "-")}`,
    };
    setFunnels([newF, ...funnels]);
    setFunnelForm({ name: "", path: "" });
    setNewFunnelOpen(false);
    toast.success("Funnel created successfully!");
  };

  const handleBuildWithAI = () => {
    if (!aiPrompt) {
      toast.error("Please enter your funnel requirements or business niche");
      return;
    }
    setAiGenerating(true);
    setTimeout(() => {
      const generatedFunnel = {
        id: "f-ai-" + Date.now(),
        name: aiPrompt.slice(0, 24) + " (AI Generated)",
        lastUpdated: "Just now",
        stepsCount: "2 Steps",
        visitors: 1,
        optins: 0,
        status: "Published",
        url: `https://deck.weddingsthyzfoto.com/funnel/ai-landing-${Date.now()}`,
      };
      setFunnels([generatedFunnel, ...funnels]);
      setAiGenerating(false);
      setAiFunnelOpen(false);
      setAiPrompt("");
      toast.success("AI Funnel generated with landing page and booking checkout!");
    }, 1200);
  };

  const handleDeleteFunnel = (id) => {
    if (window.confirm("Delete this funnel permanently?")) {
      setFunnels(prev => prev.filter(f => f.id !== id));
      toast.success("Funnel deleted");
    }
  };

  const filteredFunnels = funnels.filter(f =>
    f.name.toLowerCase().includes(searchFunnel.toLowerCase())
  );

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-[1600px] mx-auto w-full font-sans" data-testid="sites-page">
      {/* ─────────────────────────────────────────────────────────────
          TAB 1: FUNNELS (EXACT MATCH TO REFERENCE SCREENSHOT)
      ───────────────────────────────────────────────────────────── */}
      {currentTab === "funnels" && (
        <div className="space-y-6">
          {/* Top Funnel Title & Primary Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                Funnels
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Create and manage funnels to generate leads, appointments and receive payments.
              </p>
            </div>

            <div className="flex items-center gap-2">
              {/* Folder Category Button */}
              <button
                onClick={() => toast.info("Funnel folders & categories")}
                className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-50 flex items-center justify-center transition-colors shadow-2xs"
                title="Folders"
              >
                <Folder className="w-4 h-4" />
              </button>

              {/* Build with AI Button */}
              <button
                onClick={() => setAiFunnelOpen(true)}
                className="h-8 px-3 rounded-lg border border-purple-200 dark:border-purple-800 bg-white dark:bg-slate-900 text-purple-700 dark:text-purple-300 font-semibold text-xs flex items-center gap-1.5 hover:bg-purple-50 dark:hover:bg-purple-950/40 transition-colors shadow-2xs cursor-pointer"
              >
                <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                <span>Build with AI</span>
              </button>

              {/* + New Funnel Blue Button */}
              <button
                onClick={() => setNewFunnelOpen(true)}
                className="h-8 px-3.5 rounded-lg bg-[#155eef] hover:bg-blue-700 text-white font-semibold text-xs flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>New funnel</span>
              </button>
            </div>
          </div>

          {/* Card Container (Wrapping Toolbar + Table + Pagination Matching Reference Screenshot) */}
          <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden bg-white dark:bg-slate-900 shadow-xs">
            {/* Sub-toolbar Row: Breadcrumb, Recents, View Toggle, Search */}
            <div className="p-3 px-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              {/* Left: Home outline icon */}
              <div className="flex items-center gap-2">
                <button
                  className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
                  title="Home Directory"
                >
                  <Home className="w-4 h-4" />
                </button>
              </div>

              {/* Right: Clock (Recents), List View, Search */}
              <div className="flex items-center gap-2">
                <button
                  className="w-7 h-7 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-center text-slate-500 hover:text-slate-800"
                  title="Recent funnels"
                >
                  <Clock className="w-3.5 h-3.5" />
                </button>

                <button
                  className="w-7 h-7 rounded border border-blue-400 bg-blue-50 dark:bg-blue-950/40 text-blue-600 flex items-center justify-center"
                  title="List view"
                >
                  <List className="w-3.5 h-3.5" />
                </button>

                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search for funnels"
                    value={searchFunnel}
                    onChange={(e) => setSearchFunnel(e.target.value)}
                    className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 w-56 sm:w-64 outline-none focus:border-blue-500 transition-all"
                  />
                </div>
              </div>
            </div>

            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 text-[11px] font-semibold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 select-none">
                  <th className="py-3 px-4 w-1/2">Name</th>
                  <th className="py-3 px-4">Last updated</th>
                  <th className="py-3 px-4">Funnel steps</th>
                  <th className="py-3 px-4 text-right w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80 text-xs">
                {filteredFunnels.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-14 text-center text-slate-400">
                      <div className="flex flex-col items-center gap-2">
                        <Layout className="w-8 h-8 text-slate-300" />
                        <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No funnels yet</p>
                        <p className="text-xs text-slate-400">Click <strong>+ New funnel</strong> or <strong>Build with AI</strong> to create your first conversion page.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredFunnels.map((funnel) => (
                  <tr
                    key={funnel.id}
                    className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors group"
                  >
                    <td className="py-3 px-4 font-semibold text-slate-900 dark:text-slate-100">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingFunnel(funnel)}
                          className="hover:text-blue-600 font-semibold cursor-pointer text-left transition-colors"
                        >
                          {funnel.name}
                        </button>
                        <a
                          href={funnel.url.replace("https://deck.weddingsthyzfoto.com", "")}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 transition-opacity"
                          title="Open live funnel"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-500 dark:text-slate-400 font-normal">
                      {funnel.lastUpdated}
                    </td>
                    <td className="py-3 px-4 text-slate-700 dark:text-slate-300 font-medium">
                      {funnel.stepsCount}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-700">
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40 text-xs">
                          <DropdownMenuItem onClick={() => setEditingFunnel(funnel)}>
                            <Edit3 className="w-3.5 h-3.5 mr-2" /> Edit Steps
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(funnel.url.replace("https://deck.weddingsthyzfoto.com", ""), "_blank")}>
                            <ExternalLink className="w-3.5 h-3.5 mr-2" /> View Live
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toast.success("Funnel duplicated")}>
                            <Copy className="w-3.5 h-3.5 mr-2" /> Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDeleteFunnel(funnel.id)} className="text-red-600">
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
              </tbody>
            </table>

            {/* Table Footer / Pagination Matching Screenshot */}
            <div className="p-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-1.5">
                <span>Rows per page</span>
                <button className="flex items-center gap-1 font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 px-2 py-0.5 rounded">
                  <span>{rowsPerPage}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>

              <span>1 - {filteredFunnels.length} of {filteredFunnels.length}</span>

              <div className="flex items-center gap-1">
                <button
                  disabled
                  className="px-2 py-1 text-slate-300 dark:text-slate-600 cursor-not-allowed font-medium"
                >
                  Previous
                </button>
                <button className="px-2.5 py-0.5 rounded border border-blue-600 text-blue-600 font-semibold bg-blue-50 dark:bg-blue-950/30">
                  1
                </button>
                <button
                  disabled
                  className="px-2 py-1 text-slate-300 dark:text-slate-600 cursor-not-allowed font-medium"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 2: WEBSITES
      ───────────────────────────────────────────────────────────── */}
      {currentTab === "websites" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Websites</h2>
              <p className="text-xs text-slate-500 mt-1">Full multi-page websites and custom domains.</p>
            </div>
            <Button size="sm" className="bg-blue-600 text-white gap-1.5" onClick={() => setNewWebsiteOpen(true)}>
              <Plus className="w-4 h-4" /> New Website
            </Button>
          </div>

          {websites.length === 0 ? (
            <div className="border border-dashed rounded-xl p-14 text-center bg-white dark:bg-slate-900 flex flex-col items-center gap-2">
              <Globe className="w-8 h-8 text-slate-300" />
              <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No websites created yet</p>
              <p className="text-xs text-slate-400">Click <strong>+ New Website</strong> to build your multi-page studio website.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {websites.map(w => (
                <Card key={w.id} className="p-5 border space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-sm text-slate-900 dark:text-white">{w.name}</h3>
                      <a href={`https://${w.domain}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 flex items-center gap-1 mt-0.5 hover:underline">
                        {w.domain} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">{w.status}</Badge>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs text-slate-500">
                    <div className="flex items-center gap-3">
                      <span>{w.pages} Pages</span>
                      <span>{w.visitors} Visitors</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs font-semibold text-blue-600"
                        onClick={() => { setBuilderSite(w); setBuilderOpen(true); }}
                      >
                        <Edit3 className="w-3 h-3 mr-1" /> Edit in Builder
                      </Button>
                      <a
                        href={`https://${w.domain}`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 3: STORES & E-COMMERCE
      ───────────────────────────────────────────────────────────── */}
      {currentTab === "stores" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">E-Commerce Stores</h2>
              <p className="text-xs text-slate-500 mt-1">Digital products, physical checkout and Stripe paywall stores.</p>
            </div>
            <Button size="sm" className="bg-blue-600 text-white gap-1.5" onClick={() => setNewStoreOpen(true)}>
              <Plus className="w-4 h-4" /> Create Store
            </Button>
          </div>

          {stores.length === 0 ? (
            <div className="border border-dashed rounded-xl p-14 text-center bg-white dark:bg-slate-900 flex flex-col items-center gap-2">
              <ShoppingBag className="w-8 h-8 text-slate-300" />
              <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No stores created yet</p>
              <p className="text-xs text-slate-400">Click <strong>+ Create Store</strong> to setup an e-commerce checkout.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stores.map(s => (
                <div key={s.id} className="border rounded-lg p-6 bg-white dark:bg-slate-900 space-y-3">
                  <div className="flex items-center gap-3">
                    <ShoppingBag className="w-8 h-8 text-blue-600" />
                    <div>
                      <h4 className="font-bold text-sm">{s.name}</h4>
                      <p className="text-xs text-slate-500">Connected with Stripe checkout & automated digital fulfillment</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 4: WEBINARS
      ───────────────────────────────────────────────────────────── */}
      {currentTab === "webinars" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Webinars & Video Funnels</h2>
              <p className="text-xs text-slate-500 mt-1">Automated evergreen and live broadcast sales presentations.</p>
            </div>
            <Button size="sm" className="bg-blue-600 text-white gap-1.5" onClick={() => setNewWebinarOpen(true)}>
              <Plus className="w-4 h-4" /> New Webinar
            </Button>
          </div>

          {webinars.length === 0 ? (
            <div className="border border-dashed rounded-xl p-14 text-center bg-white dark:bg-slate-900 flex flex-col items-center gap-2">
              <Video className="w-8 h-8 text-slate-300" />
              <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No webinars scheduled yet</p>
              <p className="text-xs text-slate-400">Click <strong>+ New Webinar</strong> to create an automated video sales presentation.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {webinars.map(wb => (
                <Card key={wb.id} className="p-5 border space-y-3">
                  <h4 className="font-bold text-sm">{wb.name}</h4>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 5: CHAT WIDGET
      ───────────────────────────────────────────────────────────── */}
      {currentTab === "chat" && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Web Chat Widget</h2>
            <p className="text-xs text-slate-500 mt-1">Embed live AI chat and SMS lead capture on your external websites.</p>
          </div>

          <Card className="p-6 border space-y-4">
            <h3 className="font-bold text-sm">Embed Code</h3>
            <div className="p-3 rounded-lg bg-slate-950 font-sans text-xs text-emerald-400 flex items-center justify-between overflow-x-auto">
              <code>{chatEmbedScript}</code>
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-slate-800 ml-2"
                onClick={() => {
                  navigator.clipboard.writeText(chatEmbedScript);
                  setCopiedCode(true);
                  toast.success("Script copied to clipboard!");
                  setTimeout(() => setCopiedCode(false), 2000);
                }}
              >
                {copiedCode ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB: SITES & FUNNEL ANALYTICS
      ───────────────────────────────────────────────────────────── */}
      {currentTab === "analytics" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Sites & Funnel Analytics</h2>
              <p className="text-xs text-slate-500 mt-1">Real-time visitor conversion tracking, revenue attribution, and funnel drop-off metrics.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="text-xs">Last 30 Days</Button>
              <Button size="sm" className="bg-blue-600 text-white text-xs gap-1.5" onClick={() => toast.success("Analytics report downloaded (CSV)")}>
                <Download className="w-3.5 h-3.5" /> Export Data
              </Button>
            </div>
          </div>

          {/* Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 border space-y-1">
              <span className="text-xs font-semibold text-slate-500 uppercase">Page Views</span>
              <div className="text-2xl font-black text-slate-900 dark:text-white">18,420</div>
              <div className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> +24.8% vs last month
              </div>
            </Card>

            <Card className="p-4 border space-y-1">
              <span className="text-xs font-semibold text-slate-500 uppercase">Unique Visitors</span>
              <div className="text-2xl font-black text-slate-900 dark:text-white">12,850</div>
              <div className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> +18.2% vs last month
              </div>
            </Card>

            <Card className="p-4 border space-y-1">
              <span className="text-xs font-semibold text-slate-500 uppercase">Lead Opt-in Rate</span>
              <div className="text-2xl font-black text-blue-600">14.2%</div>
              <div className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> 599 Opt-ins captured
              </div>
            </Card>

            <Card className="p-4 border space-y-1">
              <span className="text-xs font-semibold text-slate-500 uppercase">Funnel Revenue</span>
              <div className="text-2xl font-black text-emerald-600">$4,850.00</div>
              <div className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> 42 Stripe checkouts
              </div>
            </Card>
          </div>

          {/* Conversion Breakdown Table */}
          <Card className="p-6 border space-y-4">
            <h3 className="font-bold text-sm text-slate-900 dark:text-white">Funnel Conversion Attribution</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-slate-400 font-semibold uppercase text-[10px]">
                    <th className="text-left pb-2">Funnel / Page</th>
                    <th className="text-left pb-2">Visitors</th>
                    <th className="text-left pb-2">Submissions</th>
                    <th className="text-left pb-2">Conversion</th>
                    <th className="text-right pb-2">Attributed Sales</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  <tr>
                    <td className="py-2.5 font-semibold text-slate-800 dark:text-slate-100">New Landing (RP)</td>
                    <td className="py-2.5">1,420</td>
                    <td className="py-2.5 font-medium text-emerald-600">384</td>
                    <td className="py-2.5">27.0%</td>
                    <td className="py-2.5 text-right font-bold text-slate-900 dark:text-white">$1,850.00</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 font-semibold text-slate-800 dark:text-slate-100">RealHUB Menu Opt-In</td>
                    <td className="py-2.5">890</td>
                    <td className="py-2.5 font-medium text-emerald-600">215</td>
                    <td className="py-2.5">24.1%</td>
                    <td className="py-2.5 text-right font-bold text-slate-900 dark:text-white">$3,000.00</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB: BLOGS & CONTENT
      ───────────────────────────────────────────────────────────── */}
      {currentTab === "blogs" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Blog & Editorial CMS</h2>
              <p className="text-xs text-slate-500 mt-1">SEO articles, client education stories, and thought leadership publishing.</p>
            </div>
            <Button size="sm" className="bg-blue-600 text-white gap-1.5" onClick={() => setNewBlogOpen(true)}>
              <Plus className="w-4 h-4" /> New Blog Post
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {blogs.map(b => (
              <Card key={b.id} className="p-5 border space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className="text-[10px] mb-1.5 text-blue-600 border-blue-200">{b.category}</Badge>
                    <h3 className="font-bold text-sm text-slate-900 dark:text-white">{b.title}</h3>
                    <p className="text-xs text-slate-500 mt-1">By {b.author} · {b.date}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">{b.status}</Badge>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-slate-100 text-xs">
                  <span className="text-slate-400">{b.views} Reads</span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600" onClick={() => toast.success(`Draft editor opened for "${b.title}"`)}>
                      Edit Post
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => { setBlogs(blogs.filter(x => x.id !== b.id)); toast.success("Article deleted"); }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          OTHER TABS: GENERAL CONTAINER
      ───────────────────────────────────────────────────────────── */}
      {!["funnels", "websites", "stores", "webinars", "analytics", "blogs", "chat"].includes(currentTab) && (
        <div className="space-y-6">
          <div className="flex items-center justify-between pb-4 border-b">
            <div>
              <h2 className="text-xl font-bold capitalize text-slate-900 dark:text-white">
                {currentTab.replace("-", " ")}
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Configure your {currentTab.replace("-", " ")} engine and automated pipelines.
              </p>
            </div>
            <Button size="sm" className="bg-blue-600 text-white gap-1.5">
              <Plus className="w-4 h-4" /> Add {currentTab.replace("-", " ")}
            </Button>
          </div>

          <Card className="p-8 text-center space-y-3">
            <Layers className="w-10 h-10 text-blue-600 mx-auto" />
            <h3 className="font-bold text-sm">{currentTab.replace("-", " ").toUpperCase()} Ready</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              All settings, integrations, and web assets for {currentTab.replace("-", " ")} are synced live.
            </p>
          </Card>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: + NEW FUNNEL
      ───────────────────────────────────────────────────────────── */}
      <Dialog open={newFunnelOpen} onOpenChange={setNewFunnelOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Funnel</DialogTitle>
            <DialogDescription>
              Set a name and slug URL for your conversion funnel.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateFunnel} className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Funnel Name *
              </label>
              <Input
                placeholder="e.g. VIP Consultation Funnel"
                value={funnelForm.name}
                onChange={(e) => setFunnelForm(f => ({ ...f, name: e.target.value }))}
                required
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                URL Slug (Optional)
              </label>
              <Input
                placeholder="e.g. vip-consultation"
                value={funnelForm.path}
                onChange={(e) => setFunnelForm(f => ({ ...f, path: e.target.value }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setNewFunnelOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                Create Funnel
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────
          MODAL: BUILD WITH AI
      ───────────────────────────────────────────────────────────── */}
      <Dialog open={aiFunnelOpen} onOpenChange={setAiFunnelOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <span>Build Funnel with AI</span>
            </DialogTitle>
            <DialogDescription>
              Describe your offer, target audience, or industry niche. Colibri AI will generate headline copy, conversion triggers, and booking steps.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <textarea
              rows={4}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g. Luxury commercial architectural photography package with $500 retainer deposit and 3-step lead booking questionnaire..."
              className="w-full text-xs p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 outline-none focus:border-purple-500"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => setAiFunnelOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={aiGenerating || !aiPrompt.trim()}
              onClick={handleBuildWithAI}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold gap-1.5"
            >
              <Sparkles className={`w-3.5 h-3.5 ${aiGenerating ? "animate-spin" : ""}`} />
              {aiGenerating ? "Generating Funnel…" : "Generate Funnel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────
          MODAL 3: EDIT FUNNEL STEPS & LIVE VIEWER
      ───────────────────────────────────────────────────────────── */}
      <Dialog open={!!editingFunnel} onOpenChange={(open) => !open && setEditingFunnel(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Funnel Editor: {editingFunnel?.name}</DialogTitle>
            <DialogDescription>
              Manage conversion steps, slug URL, and live lead capture for this funnel.
            </DialogDescription>
          </DialogHeader>

          {editingFunnel && (
            <div className="space-y-4 py-2 text-xs">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">Funnel Name</label>
                <Input
                  value={editingFunnel.name}
                  onChange={(e) => setEditingFunnel({ ...editingFunnel, name: e.target.value })}
                  className="h-9"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">Live Published URL</label>
                <div className="flex gap-2">
                  <Input readOnly value={editingFunnel.url} className="h-9 bg-slate-50 dark:bg-slate-900 font-mono text-[11px]" />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(editingFunnel.url);
                      toast.success("Funnel URL copied");
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>

              <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <span className="font-semibold text-slate-700 dark:text-slate-300">Conversion Steps</span>
                <div className="space-y-2">
                  <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">1. Opt-In &amp; Video Headline</p>
                      <p className="text-[11px] text-slate-400">Captures name, work email, and phone number</p>
                    </div>
                    <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-200">Live</Badge>
                  </div>

                  <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">2. Discovery Booking / Payment</p>
                      <p className="text-[11px] text-slate-400">Calendar integration &amp; checkout agreement</p>
                    </div>
                    <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-200">Live</Badge>
                  </div>

                  <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">3. Thank You &amp; Confirmation</p>
                      <p className="text-[11px] text-slate-400">Automated SMS confirmation &amp; CRM contact push</p>
                    </div>
                    <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-200">Live</Badge>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (editingFunnel) {
                  window.open(editingFunnel.url.replace("https://deck.weddingsthyzfoto.com", ""), "_blank");
                }
              }}
              className="gap-1.5"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View Live Funnel
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setEditingFunnel(null)}>
                Close
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                onClick={() => {
                  setFunnels(funnels.map(f => f.id === editingFunnel.id ? editingFunnel : f));
                  setEditingFunnel(null);
                  toast.success("Funnel steps saved successfully");
                }}
              >
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────
          MODAL 4: NEW WEBSITE CREATOR
      ───────────────────────────────────────────────────────────── */}
      <Dialog open={newWebsiteOpen} onOpenChange={setNewWebsiteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Website</DialogTitle>
            <DialogDescription>
              Launch a full multi-page studio or agency website with custom domain.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateWebsite} className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Website Name *
              </label>
              <Input
                placeholder="e.g. Lumina Studio Official"
                value={websiteForm.name}
                onChange={(e) => setWebsiteForm({ ...websiteForm, name: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Domain / Subdomain
              </label>
              <Input
                placeholder="agency.colibrisocialclub.com"
                value={websiteForm.domain}
                onChange={(e) => setWebsiteForm({ ...websiteForm, domain: e.target.value })}
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Initial Page Count
              </label>
              <Input
                type="number"
                min="1"
                max="20"
                value={websiteForm.pages}
                onChange={(e) => setWebsiteForm({ ...websiteForm, pages: e.target.value })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setNewWebsiteOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                Create &amp; Publish Website
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────
          MODAL 5: NEW E-COMMERCE STORE
      ───────────────────────────────────────────────────────────── */}
      <Dialog open={newStoreOpen} onOpenChange={setNewStoreOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Setup E-Commerce Store</DialogTitle>
            <DialogDescription>
              Sell digital assets, course access, physical prints, and client retainers.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateStore} className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Store Name *
              </label>
              <Input
                placeholder="e.g. Colibri Creative Digital Store"
                value={storeForm.name}
                onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Currency
                </label>
                <select
                  className="w-full text-xs h-9 px-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                  value={storeForm.currency}
                  onChange={(e) => setStoreForm({ ...storeForm, currency: e.target.value })}
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Initial Products
                </label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={storeForm.productCount}
                  onChange={(e) => setStoreForm({ ...storeForm, productCount: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setNewStoreOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                Initialize Store
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────
          MODAL 6: NEW WEBINAR
      ───────────────────────────────────────────────────────────── */}
      <Dialog open={newWebinarOpen} onOpenChange={setNewWebinarOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule New Webinar</DialogTitle>
            <DialogDescription>
              Create an evergreen automated or live broadcast sales presentation.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateWebinar} className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Webinar Title *
              </label>
              <Input
                placeholder="e.g. How to Scale Commercial Media in 2026"
                value={webinarForm.name}
                onChange={(e) => setWebinarForm({ ...webinarForm, name: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Host / Speaker Name
              </label>
              <Input
                value={webinarForm.host}
                onChange={(e) => setWebinarForm({ ...webinarForm, host: e.target.value })}
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Broadcast Date &amp; Time
              </label>
              <Input
                value={webinarForm.time}
                onChange={(e) => setWebinarForm({ ...webinarForm, time: e.target.value })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setNewWebinarOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                Schedule Webinar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Interactive Website Visual Builder Modal ── */}
      <Dialog open={builderOpen} onOpenChange={setBuilderOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                <span>Visual Studio Website Builder: {builderSite?.name}</span>
              </span>
              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700">Live Preview</Badge>
            </DialogTitle>
            <DialogDescription>
              Drag & edit sections, customize typography, bind domain {builderSite?.domain}, and publish live changes with zero downtime.
            </DialogDescription>
          </DialogHeader>

          {/* Builder Canvas Preview */}
          <div className="border rounded-xl overflow-hidden shadow-inner bg-slate-900 text-white p-6 space-y-8 my-2">
            {/* Nav */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-800">
              <div className="font-black text-base tracking-tight text-blue-400">{builderSite?.name || "Colibri Studio"}</div>
              <div className="flex items-center gap-4 text-xs text-slate-300">
                <span>Portfolio</span>
                <span>Services</span>
                <span>About</span>
                <span>Contact</span>
                <span className="px-3 py-1 rounded-full bg-blue-600 text-white font-semibold">Book Discovery</span>
              </div>
            </div>

            {/* Hero Section */}
            <div className="py-12 text-center max-w-2xl mx-auto space-y-4">
              <div className="inline-block px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-semibold">
                High-End Commercial & Editorial Media
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
                Transforming Your Brand Vision Into Cinematic Impact.
              </h1>
              <p className="text-xs sm:text-sm text-slate-400">
                Autonomous agency infrastructure, CRM client portals, and bespoke media production.
              </p>
              <div className="flex justify-center gap-3 pt-2">
                <button className="px-5 py-2 rounded-lg bg-blue-600 text-white text-xs font-bold shadow-md">
                  Explore Work
                </button>
                <button className="px-5 py-2 rounded-lg bg-slate-800 text-white text-xs font-semibold border border-slate-700">
                  Request Quote
                </button>
              </div>
            </div>

            {/* Features 3-Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-800">
              <div className="p-4 rounded-xl bg-slate-800/60 border border-slate-700 space-y-1">
                <div className="font-bold text-xs text-blue-400">01. Visual Branding</div>
                <p className="text-[11px] text-slate-400">4K commercial footage, editorial stills, and social cut-downs.</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-800/60 border border-slate-700 space-y-1">
                <div className="font-bold text-xs text-blue-400">02. AI Pipelines</div>
                <p className="text-[11px] text-slate-400">Speed-to-lead auto SMS, automated reviews, and Stripe checkout.</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-800/60 border border-slate-700 space-y-1">
                <div className="font-bold text-xs text-blue-400">03. Custom Domain</div>
                <p className="text-[11px] text-slate-400">Instant SSL provisioning on your own brand URL with 0 external badges.</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setBuilderOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setBuilderOpen(false);
                toast.success(`Changes to "${builderSite?.name}" published to ${builderSite?.domain} live!`);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              Save & Publish Live
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── New Blog Post Modal ── */}
      <Dialog open={newBlogOpen} onOpenChange={setNewBlogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Write New Blog Post</DialogTitle>
            <DialogDescription>
              Publish an SEO article to your website and client learning hub.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateBlog} className="space-y-3 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Article Title *
              </label>
              <Input
                placeholder="e.g. 5 Strategies to Scale Creative Retainers"
                value={blogForm.title}
                onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Category
                </label>
                <Input
                  value={blogForm.category}
                  onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Author
                </label>
                <Input
                  value={blogForm.author}
                  onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setNewBlogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-blue-600 text-white font-semibold">
                Publish Post
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
